import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";
import { Language } from "./identity";

// ---------------------------------------------------------------------------
// Feed: what a host sends with PUT /v1/tenants/{id}/knowledge/{kind}
// ---------------------------------------------------------------------------

export const KNOWLEDGE_KINDS = [
  "products",
  "services",
  "hours",
  "news",
  "reviews",
  "sales",
  "availability",
  "photos",
  "locations",
  "coverage",
  "brands_served",
  "recalls",
] as const;
export const KnowledgeKind = z.enum(KNOWLEDGE_KINDS);
export type KnowledgeKind = z.infer<typeof KnowledgeKind>;

/** What a tenant nobody feeds may state itself. The rest takes a system behind it: reviews with consent, sales, recalls. */
export const MANUAL_KNOWLEDGE_KINDS = ["products", "services", "hours", "availability", "news", "photos"] as const;
export const ManualKnowledgeKind = z.enum(MANUAL_KNOWLEDGE_KINDS);
export type ManualKnowledgeKind = z.infer<typeof ManualKnowledgeKind>;

export const KNOWLEDGE_MODES = ["replace", "merge"] as const;
export const KnowledgeMode = z.enum(KNOWLEDGE_MODES);
export type KnowledgeMode = z.infer<typeof KnowledgeMode>;

/** Bounded by the 1 MB body limit as well. A longer catalog goes in several `merge` calls, each with a higher version. */
export const MAX_KNOWLEDGE_ITEMS = 2_000;
export const MAX_ITEM_EXTRA_BYTES = 16_384;

const ItemKey = z.string().trim().min(1).max(200);
const Short = z.string().trim().min(1).max(300);
const Text = z.string().trim().min(1).max(5_000);
const MoneyMinor = z.number().int().min(0).max(1_000_000_000_000);
const Currency = z.string().regex(/^[A-Z]{3}$/, "ISO 4217");
/** Offsets are accepted: hosts send local time with its offset, and the service stores UTC. */
const FeedInstant = z.iso.datetime({ offset: true });
const HttpUrl = z.url({ protocol: /^https?$/ }).max(2_000);
const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");
const LocalDate = z.iso.date();
/** ISO weekday: 1 is Monday, 7 is Sunday. */
const IsoWeekday = z.number().int().min(1).max(7);
const Tags = z.array(z.string().trim().min(1).max(60)).max(50);
const Extra = z.record(z.string(), z.unknown());

const base = { key: ItemKey, extra: Extra.optional(), lang: Language.optional() };

function moneyPair(v: { priceMinor?: number; currency?: string }): boolean {
  return v.priceMinor === undefined || v.currency !== undefined;
}

function ordered(from: string | undefined, until: string | undefined): boolean {
  return !from || !until || new Date(from).getTime() <= new Date(until).getTime();
}

export const FeedPhoto = z.object({ url: HttpUrl, alt: z.string().trim().max(300).optional(), assetRef: z.string().max(200).optional() });

export const FeedOffer = z
  .object({
    label: Short,
    priceMinor: MoneyMinor.optional(),
    currency: Currency.optional(),
    discountPercent: z.number().min(0).max(100).optional(),
    validFrom: FeedInstant.optional(),
    validUntil: FeedInstant.optional(),
  })
  .refine(moneyPair, { path: ["currency"], message: "currency is required with a price" })
  .refine((v) => ordered(v.validFrom, v.validUntil), { path: ["validUntil"], message: "validUntil is before validFrom" });

export const ProductItem = z
  .object({
    ...base,
    name: Short,
    category: Short.optional(),
    description: Text.optional(),
    priceMinor: MoneyMinor.optional(),
    currency: Currency.optional(),
    /** After this instant the stated price is no longer a fact. */
    priceValidUntil: FeedInstant.optional(),
    photos: z.array(FeedPhoto).max(20).optional(),
    available: z.boolean().optional(),
    tags: Tags.optional(),
    offer: FeedOffer.optional(),
  })
  .refine(moneyPair, { path: ["currency"], message: "currency is required with a price" });
export type ProductItem = z.infer<typeof ProductItem>;

export const ServiceItem = z
  .object({
    ...base,
    name: Short,
    category: Short.optional(),
    description: Text.optional(),
    priceMinor: MoneyMinor.optional(),
    priceFromMinor: MoneyMinor.optional(),
    priceToMinor: MoneyMinor.optional(),
    currency: Currency.optional(),
    /** How many real prices a range was computed from, which the regulated verticals must state. */
    sampleSize: z.number().int().min(1).optional(),
    durationMinutes: z.number().int().min(1).max(100_000).optional(),
    available: z.boolean().optional(),
    tags: Tags.optional(),
  })
  .refine((v) => !(v.priceMinor !== undefined && (v.priceFromMinor !== undefined || v.priceToMinor !== undefined)), {
    path: ["priceMinor"],
    message: "a price or a range, not both",
  })
  .refine((v) => (v.priceFromMinor === undefined) === (v.priceToMinor === undefined), { path: ["priceToMinor"], message: "a range needs both ends" })
  .refine((v) => v.priceFromMinor === undefined || v.priceToMinor === undefined || v.priceFromMinor <= v.priceToMinor, {
    path: ["priceToMinor"],
    message: "priceToMinor is below priceFromMinor",
  })
  .refine((v) => (v.priceMinor === undefined && v.priceFromMinor === undefined) || v.currency !== undefined, {
    path: ["currency"],
    message: "currency is required with a price",
  });
export type ServiceItem = z.infer<typeof ServiceItem>;

export const HoursItem = z.object({
  ...base,
  locationKey: ItemKey.optional(),
  weekly: z.array(z.object({ day: IsoWeekday, open: LocalTime, close: LocalTime })).max(50),
  exceptions: z
    .array(
      z.object({
        date: LocalDate,
        closed: z.boolean().default(false),
        open: LocalTime.optional(),
        close: LocalTime.optional(),
        label: Short.optional(),
      }),
    )
    .max(200)
    .optional(),
});
export type HoursItem = z.infer<typeof HoursItem>;

export const NEWS_TYPES = ["news", "offer", "event"] as const;
export const NewsItem = z
  .object({
    ...base,
    type: z.enum(NEWS_TYPES).default("news"),
    title: Short,
    body: Text.optional(),
    url: HttpUrl.optional(),
    publishedAt: FeedInstant.optional(),
    validFrom: FeedInstant.optional(),
    validUntil: FeedInstant.optional(),
    photos: z.array(FeedPhoto).max(20).optional(),
  })
  .refine((v) => ordered(v.validFrom, v.validUntil), { path: ["validUntil"], message: "validUntil is before validFrom" });
export type NewsItem = z.infer<typeof NewsItem>;

export const ReviewItem = z.object({
  ...base,
  text: Text,
  rating: z.number().min(1).max(5).optional(),
  /** How the author agreed to be shown, never a full identity. */
  authorDisplay: z.string().trim().max(120).optional(),
  /** Without it the review stays out of the facts, and therefore out of every generated text. */
  consent: z.boolean(),
  source: z.string().trim().max(60).optional(),
  reviewedAt: FeedInstant.optional(),
});
export type ReviewItem = z.infer<typeof ReviewItem>;

export const SalesItem = z
  .object({
    ...base,
    periodFrom: FeedInstant,
    periodTo: FeedInstant,
    currency: Currency.optional(),
    perProduct: z
      .array(
        z.object({
          productKey: ItemKey,
          name: Short.optional(),
          quantity: z.number().min(0),
          revenueMinor: MoneyMinor.optional(),
        }),
      )
      .max(5_000)
      .default([]),
    perHourOfWeek: z
      .array(
        z.object({
          day: IsoWeekday,
          hour: z.number().int().min(0).max(23),
          orders: z.number().int().min(0),
          revenueMinor: MoneyMinor.optional(),
        }),
      )
      .max(168)
      .default([]),
  })
  .refine((v) => ordered(v.periodFrom, v.periodTo), { path: ["periodTo"], message: "periodTo is before periodFrom" });
export type SalesItem = z.infer<typeof SalesItem>;

export const AVAILABILITY_SUBJECTS = ["products", "services"] as const;
export const AvailabilityItem = z.object({
  ...base,
  subjectKey: ItemKey,
  subjectKind: z.enum(AVAILABILITY_SUBJECTS).default("products"),
  available: z.boolean(),
  /** When `available` is false: the moment it is expected back. Absent means until the host says otherwise. */
  until: FeedInstant.optional(),
  quantity: z.number().min(0).optional(),
});
export type AvailabilityItem = z.infer<typeof AvailabilityItem>;

export const PhotoItem = z
  .object({
    ...base,
    url: HttpUrl,
    alt: z.string().trim().max(300).optional(),
    assetRef: z.string().max(200).optional(),
    subjectKey: ItemKey.optional(),
    subjectKind: z.enum(AVAILABILITY_SUBJECTS).optional(),
    tags: Tags.optional(),
    /** The picture shows a person or a client's property. */
    containsCustomer: z.boolean().optional(),
    customerConsent: z.boolean().optional(),
  });
export type PhotoItem = z.infer<typeof PhotoItem>;

export const LocationItem = z.object({
  ...base,
  name: Short,
  address: z.string().trim().max(500).optional(),
  city: z.string().trim().max(120).optional(),
  country: z.string().regex(/^[A-Z]{2}$/).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  phone: z.string().trim().max(40).optional(),
  websiteUrl: HttpUrl.optional(),
  orderUrl: HttpUrl.optional(),
  bookingUrl: HttpUrl.optional(),
});
export type LocationItem = z.infer<typeof LocationItem>;

/** Aggregates only. Fields that name a person are refused by the guard in `packages/knowledge`. */
export const CoverageItem = z.object({
  ...base,
  metric: z.string().regex(/^[a-z][a-z0-9_]{0,59}$/),
  label: Short,
  value: z.number().min(0),
  unit: z.string().trim().max(40).optional(),
  region: z.string().trim().max(120).optional(),
  category: z.string().trim().max(120).optional(),
  sampleSize: z.number().int().min(1).optional(),
  periodFrom: FeedInstant.optional(),
  periodTo: FeedInstant.optional(),
});
export type CoverageItem = z.infer<typeof CoverageItem>;

export const BrandServedItem = z.object({ ...base, name: Short, models: z.array(Short).max(200).optional(), notes: z.string().trim().max(500).optional() });
export type BrandServedItem = z.infer<typeof BrandServedItem>;

export const RecallItem = z
  .object({
    ...base,
    title: Short,
    description: Text.optional(),
    brand: Short.optional(),
    models: z.array(Short).max(200).optional(),
    reference: z.string().trim().max(120).optional(),
    url: HttpUrl.optional(),
    validFrom: FeedInstant.optional(),
    validUntil: FeedInstant.optional(),
  })
  .refine((v) => ordered(v.validFrom, v.validUntil), { path: ["validUntil"], message: "validUntil is before validFrom" });
export type RecallItem = z.infer<typeof RecallItem>;

export const KNOWLEDGE_ITEM_SCHEMAS = {
  products: ProductItem,
  services: ServiceItem,
  hours: HoursItem,
  news: NewsItem,
  reviews: ReviewItem,
  sales: SalesItem,
  availability: AvailabilityItem,
  photos: PhotoItem,
  locations: LocationItem,
  coverage: CoverageItem,
  brands_served: BrandServedItem,
  recalls: RecallItem,
} as const;

export type KnowledgeItemOf<K extends KnowledgeKind> = z.infer<(typeof KNOWLEDGE_ITEM_SCHEMAS)[K]>;
export type KnowledgeItemInput<K extends KnowledgeKind> = z.input<(typeof KNOWLEDGE_ITEM_SCHEMAS)[K]>;
export type AnyKnowledgeItem = KnowledgeItemOf<KnowledgeKind>;

export type KnowledgeItemParse = { ok: true; item: AnyKnowledgeItem } | { ok: false; issues: Array<{ path: string; message: string }> };

/** Validates one item of a kind and moves the fields the contract does not know into `extra`, so a newer host loses nothing. */
export function parseKnowledgeItem(kind: KnowledgeKind, raw: unknown): KnowledgeItemParse {
  const schema = KNOWLEDGE_ITEM_SCHEMAS[kind];
  const result = schema.safeParse(raw);
  if (!result.success) {
    return { ok: false, issues: result.error.issues.map((i) => ({ path: i.path.join("."), message: i.message })) };
  }
  const known = schema.shape as Record<string, unknown>;
  const unknownFields = Object.entries(raw as Record<string, unknown>).filter(([field]) => !(field in known));
  const item = result.data as AnyKnowledgeItem;
  if (unknownFields.length === 0) return { ok: true, item };
  const extra = { ...item.extra, ...Object.fromEntries(unknownFields) };
  if (JSON.stringify(extra).length > MAX_ITEM_EXTRA_BYTES) {
    return { ok: false, issues: [{ path: "extra", message: `unknown fields exceed ${MAX_ITEM_EXTRA_BYTES} bytes` }] };
  }
  return { ok: true, item: { ...item, extra } };
}

export const KnowledgePutBody = z
  .object({
    /** Increases with every change. A timestamp or a counter both work; the same version twice is a no-op. */
    version: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    mode: KnowledgeMode,
    items: z.array(z.unknown()).max(MAX_KNOWLEDGE_ITEMS),
    /** Only with `merge`: keys that no longer exist. With `replace`, whatever is not in `items` goes. */
    removeKeys: z.array(ItemKey).max(MAX_KNOWLEDGE_ITEMS).optional(),
  })
  .refine((v) => v.mode === "merge" || !v.removeKeys?.length, { path: ["removeKeys"], message: "removeKeys only goes with merge" });
export type KnowledgePutBody = z.infer<typeof KnowledgePutBody>;

export const KnowledgeDiffCounts = z.object({
  added: z.number().int(),
  updated: z.number().int(),
  removed: z.number().int(),
  unchanged: z.number().int(),
});
export type KnowledgeDiffCounts = z.infer<typeof KnowledgeDiffCounts>;

export const KNOWLEDGE_PUT_STATUSES = ["applied", "noop"] as const;
export const KnowledgePutResult = z.object({
  kind: KnowledgeKind,
  version: z.number().int(),
  status: z.enum(KNOWLEDGE_PUT_STATUSES),
  diff: KnowledgeDiffCounts,
  /** On a no-op: the body differs from what was applied at this version, which means the host reused a version by mistake. */
  contentDiffers: z.boolean(),
  facts: z.object({ created: z.number().int(), updated: z.number().int(), retired: z.number().int() }),
});
export type KnowledgePutResult = z.infer<typeof KnowledgePutResult>;

export const KnowledgeItemDto = z.object({
  id: Uuid,
  kind: KnowledgeKind,
  key: z.string(),
  data: z.record(z.string(), z.unknown()),
  version: z.number().int(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type KnowledgeItemDto = z.infer<typeof KnowledgeItemDto>;

export const KnowledgeListQuery = IdPageQuery.extend({ key: ItemKey.optional() });
export type KnowledgeListQuery = z.infer<typeof KnowledgeListQuery>;
export const KnowledgeItemPage = pageOf(KnowledgeItemDto);

/** Who last stated a kind: the host's system, a file someone uploaded, or a person typing on the screen. */
export const KNOWLEDGE_FEED_SOURCES = ["host", "csv", "manual"] as const;
export const KnowledgeFeedDto = z.object({
  kind: KnowledgeKind,
  source: z.enum(KNOWLEDGE_FEED_SOURCES).nullable(),
  version: z.number().int().nullable(),
  mode: KnowledgeMode.nullable(),
  itemCount: z.number().int(),
  appliedAt: IsoDateTime.nullable(),
});
export type KnowledgeFeedDto = z.infer<typeof KnowledgeFeedDto>;
export const KnowledgeSummary = z.object({
  feeds: z.array(KnowledgeFeedDto),
  /** A host's system states what is true here, so the screen only reads. */
  fedByHost: z.boolean(),
  hostName: z.string().nullable(),
});
export type KnowledgeSummary = z.infer<typeof KnowledgeSummary>;

export const KnowledgeChangeDto = z.object({
  id: Uuid,
  kind: KnowledgeKind,
  version: z.number().int(),
  mode: KnowledgeMode,
  counts: KnowledgeDiffCounts,
  added: z.array(z.string()),
  updated: z.array(z.object({ key: z.string(), fields: z.array(z.string()) })),
  removed: z.array(z.string()),
  createdAt: IsoDateTime,
});
export type KnowledgeChangeDto = z.infer<typeof KnowledgeChangeDto>;
export const KnowledgeChangePage = pageOf(KnowledgeChangeDto);

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

export const FACT_SOURCES = ["feed", "manual", "ai_draft"] as const;
export const FactSource = z.enum(FACT_SOURCES);
export type FactSource = z.infer<typeof FactSource>;

export const FACT_CATEGORIES = ["price", "feature", "number", "offer", "general"] as const;
export const FactCategory = z.enum(FACT_CATEGORIES);
export type FactCategory = z.infer<typeof FactCategory>;

export const FACT_STATUSES = ["draft", "approved", "retired"] as const;
export const FactStatus = z.enum(FACT_STATUSES);
export type FactStatus = z.infer<typeof FactStatus>;

/**
 * What a fact is about. Vertical archetypes name these as their required facts. The feed produces the first group;
 * the rest are written by people, because no host table holds them.
 */
export const FACT_KINDS = [
  "product",
  "product_price",
  "product_photo",
  "offer",
  "hours",
  "special_hours",
  "location",
  "order_link",
  "booking_link",
  "review",
  "news",
  "event",
  "service",
  "service_price",
  "service_duration",
  "coverage",
  "brand_served",
  "recall",
  "photo",
  "customer_photo_consented",
  "claim",
  "real_number",
  "demo_link",
  "case_study",
  "commission_comparison",
  "guide",
  "question",
  "team",
  "kitchen_story",
  "season",
  "deadline",
  "maintenance_tip",
  "symptom",
  "red_flag_list",
  "when_to_call_112",
  "what_not_to_wait_out",
  "specialty",
  "city_supply",
  "price_range_sample",
  "awareness_topic",
  "founder_point",
] as const;
export const FactKind = z.enum(FACT_KINDS);
export type FactKind = z.infer<typeof FactKind>;

const FactTexts = z.record(Language, z.string().trim().min(1).max(2_000)).refine((v) => Object.keys(v).length > 0, "at least one language");

export const FactDto = z.object({
  id: Uuid,
  /** Empty only on a review whose consent was withdrawn: the quote is erased with the fact's retirement. */
  texts: z.record(z.string(), z.string()),
  source: FactSource,
  sourceRef: z.string().nullable(),
  /** Set when an item on the knowledge screen states this: it changes there. */
  feedItem: z.string().nullable(),
  category: FactCategory,
  kind: z.string().nullable(),
  numbers: z.array(z.string()),
  data: z.record(z.string(), z.unknown()).nullable(),
  validFrom: IsoDateTime.nullable(),
  validUntil: IsoDateTime.nullable(),
  suspendedUntil: IsoDateTime.nullable(),
  status: FactStatus,
  retiredReason: z.string().nullable(),
  /** Approved, inside its validity, and not suspended: what `factsForGeneration` would hand out now. */
  usable: z.boolean(),
  usedCount: z.number().int(),
  approvedByName: z.string().nullable(),
  approvedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type FactDto = z.infer<typeof FactDto>;
export const FactPage = pageOf(FactDto);

export const FactListQuery = IdPageQuery.extend({
  status: FactStatus.optional(),
  source: FactSource.optional(),
  category: FactCategory.optional(),
  kind: FactKind.optional(),
  usable: z.enum(["true", "false"]).optional(),
});
export type FactListQuery = z.infer<typeof FactListQuery>;

export const FactCreate = z
  .object({
    texts: FactTexts,
    source: z.enum(["manual", "ai_draft"]).default("manual"),
    sourceRef: z.string().trim().max(500).optional(),
    category: FactCategory,
    kind: FactKind.optional(),
    /** Merged with the numbers found in the texts. */
    numbers: z.array(z.string().trim().min(1).max(40)).max(100).optional(),
    validFrom: IsoDateTime.optional(),
    validUntil: IsoDateTime.optional(),
  })
  .refine((v) => ordered(v.validFrom, v.validUntil), { path: ["validUntil"], message: "validUntil is before validFrom" });
export type FactCreate = z.input<typeof FactCreate>;

export const FactPatch = z
  .object({
    texts: FactTexts.optional(),
    sourceRef: z.string().trim().max(500).nullable().optional(),
    category: FactCategory.optional(),
    kind: FactKind.nullable().optional(),
    numbers: z.array(z.string().trim().min(1).max(40)).max(100).optional(),
    validFrom: IsoDateTime.nullable().optional(),
    validUntil: IsoDateTime.nullable().optional(),
  })
  .refine((v) => ordered(v.validFrom ?? undefined, v.validUntil ?? undefined), { path: ["validUntil"], message: "validUntil is before validFrom" });
export type FactPatch = z.infer<typeof FactPatch>;

export const FactGenerationQuery = z.object({
  lang: Language.optional(),
  categories: z.string().optional(),
  kinds: z.string().optional(),
  angleId: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(1_000).optional(),
});

export const FrozenFactDto = z.object({
  id: Uuid,
  category: FactCategory,
  kind: z.string().nullable(),
  source: FactSource,
  sourceRef: z.string().nullable(),
  text: z.string(),
  textLang: z.string(),
  texts: z.record(z.string(), z.string()),
  numbers: z.array(z.string()),
  data: z.record(z.string(), z.unknown()).nullable(),
  validUntil: IsoDateTime.nullable(),
});
export type FrozenFactDto = z.infer<typeof FrozenFactDto>;

export const FrozenFactsDto = z.object({
  asOf: IsoDateTime,
  lang: z.string().nullable(),
  hash: z.string(),
  truncated: z.boolean(),
  block: z.object({
    facts: z.array(z.object({ category: FactCategory, kind: z.string().nullable(), text: z.string(), numbers: z.array(z.string()) })),
  }),
  facts: z.array(FrozenFactDto),
});
export type FrozenFactsDto = z.infer<typeof FrozenFactsDto>;

// ---------------------------------------------------------------------------
// Brand kit
// ---------------------------------------------------------------------------

export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "#RRGGBB");

export const BrandColors = z.object({
  primary: HexColor.optional(),
  onPrimary: HexColor.optional(),
  secondary: HexColor.optional(),
  onSecondary: HexColor.optional(),
  accent: HexColor.optional(),
  onAccent: HexColor.optional(),
  background: HexColor.optional(),
  text: HexColor.optional(),
});
export type BrandColors = z.infer<typeof BrandColors>;

export const HashtagSet = z.object({
  name: z.string().trim().min(1).max(80),
  tags: z.array(z.string().trim().regex(/^#?[\p{L}\p{N}_]{1,80}$/u)).min(1).max(30),
});
export type HashtagSet = z.infer<typeof HashtagSet>;

const PhraseList = z.array(z.string().trim().min(1).max(200)).max(500);

/** What a tenant or an agency stores. Anything left out inherits: tenant, then agency, then the built-in default. */
export const BrandInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  tone: z.string().trim().min(1).max(2_000).optional(),
  languages: z.array(Language).min(1).max(8).optional(),
  colors: BrandColors.optional(),
  font: z.string().trim().min(1).max(120).optional(),
  logoAssetRef: z.string().trim().min(1).max(500).optional(),
  allowedCtas: PhraseList.optional(),
  forbiddenPhrases: PhraseList.optional(),
  competitors: PhraseList.optional(),
  hashtagSets: z.array(HashtagSet).max(50).optional(),
  signature: z.string().trim().min(1).max(1_000).optional(),
});
export type BrandInput = z.infer<typeof BrandInput>;

export const BRAND_FIELD_SOURCES = ["tenant", "agency", "default"] as const;

export const ContrastPair = z.object({
  foreground: z.string(),
  background: z.string(),
  ratio: z.number(),
  required: z.number(),
  ok: z.boolean(),
  /** The stored on-color failed, so the resolved brand uses black or white instead. */
  adjusted: z.boolean(),
});
export type ContrastPair = z.infer<typeof ContrastPair>;

export const ResolvedBrandDto = z.object({
  name: z.string(),
  tone: z.string().nullable(),
  languages: z.array(z.string()),
  colors: z.object({
    primary: HexColor,
    onPrimary: HexColor,
    secondary: HexColor,
    onSecondary: HexColor,
    accent: HexColor,
    onAccent: HexColor,
    background: HexColor,
    text: HexColor,
  }),
  font: z.string(),
  logoAssetRef: z.string().nullable(),
  allowedCtas: z.array(z.string()),
  forbiddenPhrases: z.array(z.string()),
  competitors: z.array(z.string()),
  hashtagSets: z.array(HashtagSet),
  signature: z.string().nullable(),
  contrast: z.array(ContrastPair),
  sources: z.record(z.string(), z.enum(BRAND_FIELD_SOURCES)),
});
export type ResolvedBrandDto = z.infer<typeof ResolvedBrandDto>;

export const BrandResponse = z.object({ stored: BrandInput.nullable(), inherited: BrandInput.nullable(), resolved: ResolvedBrandDto });
export type BrandResponse = z.infer<typeof BrandResponse>;

export const BRAND_PREVIEW_THEMES = ["light", "primary", "accent", "soft"] as const;

/** A card drawn with a brand that may not be saved yet, so a form can show what it would look like. Nothing is stored. */
export const BrandPreviewRequest = z
  .object({
    draft: BrandInput.optional(),
    theme: z.enum(BRAND_PREVIEW_THEMES).default("light"),
    lang: Language.optional(),
    /** The sample text comes from the caller, in the language of its interface. */
    card: z.object({
      title: z.string().trim().min(1).max(120),
      subtitle: z.string().trim().min(1).max(200).optional(),
      badge: z.string().trim().min(1).max(40).optional(),
    }),
  })
  .strict();
export type BrandPreviewRequest = z.input<typeof BrandPreviewRequest>;

export const BrandPreviewResponse = z.object({
  /** Base64 PNG, as the card engine drew it. */
  png: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  resolved: ResolvedBrandDto,
});
export type BrandPreviewResponse = z.infer<typeof BrandPreviewResponse>;

export const AgencyBrandResponse = z.object({
  agencyId: Uuid,
  stored: BrandInput.nullable(),
  /** What a client with nothing of its own gets from this agency. */
  effective: ResolvedBrandDto,
});
export type AgencyBrandResponse = z.infer<typeof AgencyBrandResponse>;

// ---------------------------------------------------------------------------
// Profiles and angles
// ---------------------------------------------------------------------------

const StringList = z.array(z.string().trim().min(1).max(500)).max(50);

export const ProfileInput = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  pains: StringList.default([]),
  objections: StringList.default([]),
});
export type ProfileInput = z.input<typeof ProfileInput>;
export const ProfilePatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2_000).nullable().optional(),
  pains: StringList.optional(),
  objections: StringList.optional(),
});
export type ProfilePatch = z.infer<typeof ProfilePatch>;

export const ProfileDto = z.object({
  id: Uuid,
  name: z.string(),
  description: z.string().nullable(),
  pains: z.array(z.string()),
  objections: z.array(z.string()),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ProfileDto = z.infer<typeof ProfileDto>;
export const ProfilePage = pageOf(ProfileDto);

export const ANGLE_OBJECTIVES = ["leads", "signup", "traffic", "awareness"] as const;
export const AngleObjective = z.enum(ANGLE_OBJECTIVES);
export const ANGLE_STATUSES = ["active", "paused", "closed"] as const;
export const AngleStatus = z.enum(ANGLE_STATUSES);
export type AngleStatus = z.infer<typeof AngleStatus>;

const Slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lowercase words joined by hyphens").max(80);

export const AngleInput = z.object({
  slug: Slug,
  promise: z.string().trim().min(1).max(1_000),
  landing: HttpUrl.optional(),
  objective: AngleObjective,
  factIds: z.array(Uuid).max(200).default([]),
  profileIds: z.array(Uuid).max(50).default([]),
  status: AngleStatus.default("active"),
});
export type AngleInput = z.input<typeof AngleInput>;
export const AnglePatch = z.object({
  slug: Slug.optional(),
  promise: z.string().trim().min(1).max(1_000).optional(),
  landing: HttpUrl.nullable().optional(),
  objective: AngleObjective.optional(),
  factIds: z.array(Uuid).max(200).optional(),
  profileIds: z.array(Uuid).max(50).optional(),
  status: AngleStatus.optional(),
});
export type AnglePatch = z.infer<typeof AnglePatch>;

export const AngleDto = z.object({
  id: Uuid,
  slug: z.string(),
  promise: z.string(),
  landing: z.string().nullable(),
  objective: AngleObjective,
  factIds: z.array(Uuid),
  profileIds: z.array(Uuid),
  status: AngleStatus,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AngleDto = z.infer<typeof AngleDto>;
export const AnglePage = pageOf(AngleDto);
export const AngleListQuery = IdPageQuery.extend({ status: AngleStatus.optional() });

// ---------------------------------------------------------------------------
// Verticals
// ---------------------------------------------------------------------------

/** Texts every pack and custom archetype ships in, the languages of the UI. */
const I18nName = z.object({
  ro: z.string().trim().min(1).max(120),
  bg: z.string().trim().min(1).max(120),
  hu: z.string().trim().min(1).max(120),
  en: z.string().trim().min(1).max(120),
});

export const ARCHETYPE_CHANNELS = ["facebook", "instagram", "gbp", "telegram", "linkedin", "tiktok", "email", "sms"] as const;
export const ArchetypeChannel = z.enum(ARCHETYPE_CHANNELS);
export type ArchetypeChannel = z.infer<typeof ArchetypeChannel>;

export const ARCHETYPE_VARIES = ["hook", "visual", "cta", "angle", "length", "nothing"] as const;
export const ArchetypeVaries = z.enum(ARCHETYPE_VARIES);
export type ArchetypeVaries = z.infer<typeof ArchetypeVaries>;

/** The same values as the tiers in `packages/compliance`, which sits downstream of this schema; a plugins test ties the two. */
export const AUTONOMY_DEFAULTS = ["human_only", "agent_prescreen", "agent_approve"] as const;
export const AutonomyDefault = z.enum(AUTONOMY_DEFAULTS);
export type AutonomyDefault = z.infer<typeof AutonomyDefault>;

export const ArchetypeSlug = z.string().regex(/^[a-z][a-z0-9_]{1,59}$/, "lowercase snake_case");

const I18nGuide = z.object({
  ro: z.string().trim().min(1).max(300),
  bg: z.string().trim().min(1).max(300),
  hu: z.string().trim().min(1).max(300),
  en: z.string().trim().min(1).max(300),
});

export const ArchetypeDto = z.object({
  id: ArchetypeSlug,
  name: I18nName,
  /** What this kind of post is for, and one example. The generator reads the first; a custom one may have neither. */
  purpose: I18nGuide.nullable().optional(),
  example: I18nGuide.nullable().optional(),
  requiredFactKinds: z.array(FactKind),
  cooldownDays: z.number().int().min(0).max(3650),
  defaultChannels: z.array(ArchetypeChannel).min(1),
  regulated: z.boolean(),
  varies: z.array(ArchetypeVaries).min(1),
  defaultAutonomy: AutonomyDefault,
  custom: z.boolean(),
});
export type ArchetypeDto = z.infer<typeof ArchetypeDto>;

export const CardTemplateDto = z.object({
  id: z.string(),
  name: I18nName,
  formats: z.array(z.enum(["1:1", "4:5", "9:16", "1.91:1"])).min(1),
  slots: z.array(z.string()).min(1),
});
export type CardTemplateDto = z.infer<typeof CardTemplateDto>;

export const LeadStageDto = z.object({ id: z.string(), name: I18nName, order: z.number().int(), terminal: z.boolean() });
export type LeadStageDto = z.infer<typeof LeadStageDto>;

export const VerticalDto = z.object({
  id: z.string(),
  /** False for a tenant whose vertical has no pack: it gets only its own archetypes, each with a human approver. */
  known: z.boolean(),
  name: I18nName,
  regulated: z.boolean(),
  complianceRulesets: z.array(z.string()),
  archetypes: z.array(ArchetypeDto),
  cardTemplates: z.array(CardTemplateDto),
  journeys: z.array(z.string()),
  leadStages: z.array(LeadStageDto),
  hostEvents: z.array(z.string()),
  hostActions: z.array(z.string()),
});
export type VerticalDto = z.infer<typeof VerticalDto>;

/** The tenant's own languages are enough: the service fills the rest from the first one, so every reader finds a name. */
export const CustomArchetypeName = z
  .partialRecord(z.enum(["ro", "bg", "hu", "en"]), z.string().trim().min(1).max(120))
  .refine((name) => Object.keys(name).length > 0, { message: "a name is required" });

const CustomArchetypeFields = z.object({
  name: CustomArchetypeName,
  purpose: z.string().trim().max(300).nullable(),
  example: z.string().trim().max(300).nullable(),
  requiredFactKinds: z.array(FactKind).max(20),
  cooldownDays: z.number().int().min(0).max(3650),
  defaultChannels: z.array(ArchetypeChannel).min(1).max(8),
  regulated: z.boolean(),
  varies: z.array(ArchetypeVaries).min(1).max(6),
  defaultAutonomy: AutonomyDefault,
});

export const CustomArchetypeInput = CustomArchetypeFields.extend({
  id: ArchetypeSlug,
  purpose: CustomArchetypeFields.shape.purpose.optional(),
  example: CustomArchetypeFields.shape.example.optional(),
  requiredFactKinds: CustomArchetypeFields.shape.requiredFactKinds.default([]),
  cooldownDays: CustomArchetypeFields.shape.cooldownDays.default(30),
  regulated: CustomArchetypeFields.shape.regulated.default(false),
  varies: CustomArchetypeFields.shape.varies.default(["hook"]),
  defaultAutonomy: CustomArchetypeFields.shape.defaultAutonomy.default("agent_prescreen"),
});
export type CustomArchetypeInput = z.input<typeof CustomArchetypeInput>;
/** Built on the fields without defaults: a patch that names one field must leave the others as they are. */
export const CustomArchetypePatch = CustomArchetypeFields.partial();
export type CustomArchetypePatch = z.infer<typeof CustomArchetypePatch>;
/** What the caller wrote, before any default: only that can be a request to weaken a rule. */
export const CustomArchetypeAsked = CustomArchetypeFields.pick({ regulated: true, defaultAutonomy: true }).partial();

/** What is using an archetype, so a delete can say what it will and will not touch. */
export const ArchetypeUsageDto = z.object({
  /** Plan items of plans not finished yet. They stay. */
  plannedItems: z.number().int(),
  /** Written and not out yet. They stay as they are. */
  waitingPosts: z.number().int(),
  /** Topics in the bank: they lose the kind and stay usable. */
  bankTopics: z.number().int(),
});
export type ArchetypeUsageDto = z.infer<typeof ArchetypeUsageDto>;

/** A person typing one item. The key is the server's to pick when there is none yet. */
export const ManualKnowledgeBody = z.strictObject({ item: z.record(z.string(), z.unknown()) });
export type ManualKnowledgeBody = z.infer<typeof ManualKnowledgeBody>;
export const ManualKnowledgeResult = z.object({
  item: KnowledgeItemDto,
  /** New or changed claims wait for someone to approve them. */
  factsToApprove: z.number().int(),
});
export type ManualKnowledgeResult = z.infer<typeof ManualKnowledgeResult>;
